import { useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { useUrlSyncedSearch } from '@/hooks/useUrlSyncedSearch';
import { DATE_RANGE_FILTER_KEYS } from '@/utils/filterFields';
import { toFilterText } from '@/utils/filters';
import { VERIFY_STATUS } from '../constants/billing.constants';
import type { OfflineDcrSearch } from '../types/billing.types';
import { toOfflineDcrFilterSearch, toOfflineDcrFilterValues } from '../utils/billingFilters';
import { getQueueDefaultPeriod } from '../utils/period';

const route = getRouteApi('/_auth/billing/offline-dcr-verify');

const normalizeFilterValues = (values: Record<string, string>) =>
  toOfflineDcrFilterValues(toOfflineDcrFilterSearch(values));

export function useOfflineDcrFilters() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const appliedValues = useMemo(() => toOfflineDcrFilterValues(search), [search]);
  const defaultValues = useMemo(
    () => ({ ...getQueueDefaultPeriod(), isVerify: toFilterText(VERIFY_STATUS.PENDING) }),
    [],
  );

  const filters = useUrlSyncedFilters({
    appliedValues,
    shouldWaitForApply: true,
    defaultValues,
    rangeKeys: DATE_RANGE_FILTER_KEYS,
    normalizeValues: normalizeFilterValues,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({ ...previous, ...toOfflineDcrFilterSearch(values) }),
        replace: true,
      });
    },
  });

  const params = useMemo(
    (): OfflineDcrSearch & { dateFrom: string; dateTo: string } => ({
      ...search,
      dateFrom: search.dateFrom ?? defaultValues.dateFrom,
      dateTo: search.dateTo ?? defaultValues.dateTo,
    }),
    [search, defaultValues],
  );

  const zmSearch = useUrlSyncedSearch({
    appliedText: search.search,
    onApply: (text) => {
      void navigate({
        search: (previous) => ({ ...previous, search: text }),
        replace: true,
      });
    },
  });

  return { ...filters, ...zmSearch, params };
}
